import React, { useState, useEffect } from 'react';

const DEFAULT_CHARITY = {
  name: '',
  mission: '',
  website: '',
  description: '',
  amountRaised: 0,
  lastEvent: '',
  logo: null
};

function CharityEditor({ data, onSave, showToast }) {
  const [charities, setCharities] = useState([]);
  const [editingCharity, setEditingCharity] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setCharities(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  const handleAddCharity = () => {
    setEditingCharity({ ...DEFAULT_CHARITY, isNew: true });
    setShowModal(true);
  };

  const handleEditCharity = (charity, index) => {
    setEditingCharity({ ...charity, index, isNew: false });
    setShowModal(true);
  };

  const handleDeleteCharity = (index) => {
    const charity = charities[index];
    if (!window.confirm(`Remove "${charity.name}" from charity partners?`)) {
      return;
    }
    const updated = charities.filter((_, i) => i !== index);
    setCharities(updated);
    setHasChanges(true);
    showToast('Charity removed. Click Save to confirm.', 'warning');
  };

  const handleSaveCharity = (charityData) => {
    let updated;
    if (charityData.isNew) {
      const { isNew, index, ...cleanCharity } = charityData;
      updated = [...charities, cleanCharity];
    } else {
      updated = charities.map((c, i) => {
        if (i === charityData.index) {
          const { isNew, index, ...cleanCharity } = charityData;
          return cleanCharity;
        }
        return c;
      });
    }

    setCharities(updated);
    setShowModal(false);
    setEditingCharity(null);
    setHasChanges(true);
    showToast(`Charity ${charityData.isNew ? 'added' : 'updated'}. Click Save to confirm.`, 'warning');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const result = await onSave(charities);
    setSaving(false);
    if (result.success) {
      setHasChanges(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--forest-800)', margin: 0 }}>
            Charity Partners
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginTop: '0.25rem' }}>
            {charities.length} partner{charities.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-editor btn-editor-secondary" onClick={handleAddCharity}>
            + Add Charity
          </button>
          <button
            className="btn-editor btn-editor-primary"
            onClick={handleSaveAll}
            disabled={!hasChanges || saving}
            style={{ opacity: hasChanges ? 1 : 0.5 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Charity Grid */}
      {charities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">Heart</div>
          <div className="empty-state-title">No charity partners yet</div>
          <div className="empty-state-text">Add your first charity partner to get started</div>
          <button className="btn-editor btn-editor-primary" onClick={handleAddCharity}>
            + Add Charity
          </button>
        </div>
      ) : (
        <div className="content-grid">
          {charities.map((charity, index) => (
            <CharityCard
              key={index}
              charity={charity}
              formatCurrency={formatCurrency}
              onEdit={() => handleEditCharity(charity, index)}
              onDelete={() => handleDeleteCharity(index)}
            />
          ))}
          <div className="add-card" onClick={handleAddCharity}>
            <div className="add-card-icon">+</div>
            <div className="add-card-text">Add Charity Partner</div>
          </div>
        </div>
      )}

      {/* Total Raised Summary */}
      {charities.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
          borderRadius: '1rem',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
            Total Raised Across All Partners
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: '700' }}>
            {formatCurrency(charities.reduce((sum, c) => sum + (c.amountRaised || 0), 0))}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && editingCharity && (
        <CharityModal
          charity={editingCharity}
          onSave={handleSaveCharity}
          onClose={() => {
            setShowModal(false);
            setEditingCharity(null);
          }}
        />
      )}
    </div>
  );
}

function CharityCard({ charity, formatCurrency, onEdit, onDelete }) {
  return (
    <div className="content-card" style={{ background: 'linear-gradient(135deg, white 0%, #fdf2f8 100%)' }}>
      <div className="content-card-header">
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '0.75rem',
          background: '#fce7f3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.5rem'
        }}>
          Heart
        </div>
        <div className="content-card-info" style={{ flex: 1 }}>
          <div className="content-card-name">{charity.name}</div>
          <div className="content-card-role">{charity.mission}</div>
        </div>
        <div className="content-card-actions">
          <button className="btn-editor btn-icon btn-editor-ghost" onClick={onEdit}>Edit</button>
          <button className="btn-editor btn-icon btn-editor-danger" onClick={onDelete}>X</button>
        </div>
      </div>

      <div className="content-card-body" style={{ marginTop: '0.75rem' }}>
        {charity.description}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        marginTop: '1rem',
        paddingTop: '1rem',
        borderTop: '1px solid #fce7f3'
      }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--forest-500)', marginBottom: '0.25rem' }}>
            Amount Raised
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#be185d' }}>
            {formatCurrency(charity.amountRaised || 0)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--forest-500)', marginBottom: '0.25rem' }}>
            Last Event
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--forest-700)' }}>
            {charity.lastEvent || 'No events yet'}
          </div>
        </div>
      </div>

      {charity.website && (
        <div style={{ marginTop: '0.75rem' }}>
          <a
            href={charity.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '0.75rem',
              color: 'var(--forest-500)',
              textDecoration: 'none'
            }}
          >
            {charity.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        </div>
      )}
    </div>
  );
}

function CharityModal({ charity, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: charity.name || '',
    mission: charity.mission || '',
    website: charity.website || '',
    description: charity.description || '',
    amountRaised: charity.amountRaised || 0,
    lastEvent: charity.lastEvent || '',
    logo: charity.logo || null,
    isNew: charity.isNew,
    index: charity.index
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Charity name is required';
    if (!formData.mission.trim()) newErrors.mission = 'Mission statement is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Must be a valid URL';
    }
    if (formData.amountRaised < 0) {
      newErrors.amountRaised = 'Amount cannot be negative';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        amountRaised: parseInt(formData.amountRaised) || 0
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{charity.isNew ? 'Add Charity Partner' : 'Edit Charity'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Charity Name *</label>
              <input
                type="text"
                className={`form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="St. Jude Children's Research Hospital"
              />
              {errors.name && <div className="form-error">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Mission Statement *</label>
              <input
                type="text"
                className={`form-input ${errors.mission ? 'error' : ''}`}
                value={formData.mission}
                onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                placeholder="Finding cures. Saving children."
              />
              {errors.mission && <div className="form-error">{errors.mission}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Website</label>
              <input
                type="text"
                className={`form-input ${errors.website ? 'error' : ''}`}
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.stjude.org/"
              />
              {errors.website && <div className="form-error">{errors.website}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this charity does..."
                rows={3}
              />
              {errors.description && <div className="form-error">{errors.description}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Amount Raised ($)</label>
                <input
                  type="number"
                  className={`form-input ${errors.amountRaised ? 'error' : ''}`}
                  value={formData.amountRaised}
                  onChange={(e) => setFormData({ ...formData, amountRaised: e.target.value })}
                  placeholder="0"
                  min="0"
                />
                {errors.amountRaised && <div className="form-error">{errors.amountRaised}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Last Event</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.lastEvent}
                  onChange={(e) => setFormData({ ...formData, lastEvent: e.target.value })}
                  placeholder="Holiday Charity Stream 2024"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-editor btn-editor-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-editor btn-editor-primary">
              {charity.isNew ? 'Add Charity' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CharityEditor;
